import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Transaction,
  TransactionsResult,
  TransactionsTotals,
  useGetTransactionsQuery,
  useGetTransactionsTotalsQuery,
  useLazyGetTransactionsQuery,
} from "../../../../services/transactions";
import {
  Accounts,
  getErrorMessages,
  useGetAccountsQuery,
  useGetAccountsYearsQuery,
  useGetCategoriesQuery,
} from "../../../../services";
import { PaginationType } from "../../../../services/types";
import { notify } from "../../../../components/Notification";
import {
  Category,
  Filters,
  GetTransactionsProps,
  GetTransactionsTotalsProps,
  SortConfig,
} from "../../../Transactions/types";
import { TransactionType } from "../../../../utils";
import { StringOptions } from "../../../../components/Select";

export interface UseTransactionsDataProps {
  filters: Filters;
  sortConfig: SortConfig;
  accountId?: number;
  activeTab: "transactions" | "recurring" | "graph";
}

export interface UseTransactionsDataReturn {
  transactionsData: TransactionsResult;
  totals: TransactionsTotals;
  accounts: Accounts[];
  categoriesIncome: Category[];
  categoriesExpense: Category[];
  categoryOptions: StringOptions[];
  filterYears: StringOptions[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  refetch: (loadMore?: boolean) => Promise<void>;
  refetchTotals: () => Promise<void>;
  loadMoreTransactions: () => void;
}

const DEFAULT_PAGINATION: PaginationType = {
  page: 0,
  limit: 10,
  hasNext: false,
  totalRows: 0,
};

const DEFAULT_TOTALS: TransactionsTotals = {
  totalIncome: 0,
  totalExpense: 0,
  totalNet: 0,
  transactionsCount: 0,
};

const getTransactionTypeValue = (type: string): TransactionType => {
  const TRANSACTION_TYPES = [
    { value: TransactionType.ALL, label: "All" },
    { value: TransactionType.INCOME, label: "Income" },
    { value: TransactionType.EXPENSE, label: "Expense" },
    { value: TransactionType.TRANSFER, label: "Transfer" },
    { value: TransactionType.LOAN_PAYMENT, label: "Loan Payment" },
  ];

  const found = TRANSACTION_TYPES.find((t) => t.value === type);
  return found ? found.value : TRANSACTION_TYPES[0].value;
};

// Mirrors the main Transactions page's base-query + lazy-append accumulator: the effect on
// `fulfilledTimeStamp` clears appended pages whenever the base page refetches (filter/page change or a write).
export const useTransactionsData = ({
  filters,
  sortConfig,
  accountId,
  activeTab,
}: UseTransactionsDataProps): UseTransactionsDataReturn => {
  const isEnabled = activeTab === "transactions" || activeTab === "graph";

  const { data: categoriesIncome = [] } = useGetCategoriesQuery(true);
  const { data: categoriesExpense = [] } = useGetCategoriesQuery(false);
  const { data: accounts = [] } = useGetAccountsQuery();
  const { data: availableYears = [] } = useGetAccountsYearsQuery();

  // Filter params shared by the list and totals queries (no pagination/sort).
  const queryFilters = useMemo<GetTransactionsTotalsProps>(() => {
    const type = getTransactionTypeValue(filters.transactionType.value);
    return {
      type: [type],
      searchBy: filters.searchBy,
      dateRange: filters.dateRange.value,
      year:
        filters.dateRange.value === "all"
          ? undefined
          : Number(filters.year.value),
      month:
        filters.dateRange.value === "all"
          ? undefined
          : Number(filters.month.value),
      categoryId: filters.category ? Number(filters.category.value) : undefined,
      accountId,
    };
  }, [
    filters.transactionType,
    filters.searchBy,
    filters.dateRange,
    filters.year,
    filters.month,
    filters.category,
    accountId,
  ]);

  const transactionParams = useMemo<GetTransactionsProps>(
    () => ({
      ...queryFilters,
      sortBy: sortConfig.field,
      orderBy: sortConfig.order,
      page: filters.page,
      rowsPerPage: filters.rowsPerPage,
    }),
    [queryFilters, sortConfig, filters.page, filters.rowsPerPage],
  );

  const base = useGetTransactionsQuery(transactionParams, { skip: !isEnabled });
  const [fetchMore, lazyResult] = useLazyGetTransactionsQuery();
  const totalsQuery = useGetTransactionsTotalsQuery(queryFilters, {
    skip: !isEnabled,
  });

  const [appended, setAppended] = useState<Transaction[]>([]);
  const [nextPage, setNextPage] = useState(filters.page + 1);
  const [latestPagination, setLatestPagination] = useState<
    PaginationType | undefined
  >(base.data?.pagination);

  useEffect(() => {
    setAppended([]);
    setNextPage(filters.page + 1);
    setLatestPagination(base.data?.pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base.fulfilledTimeStamp]);

  const pagination = latestPagination ?? base.data?.pagination;

  const transactionsData = useMemo<TransactionsResult>(
    () => ({
      transactions: [...(base.data?.transactions ?? []), ...appended],
      pagination: pagination ?? DEFAULT_PAGINATION,
    }),
    [base.data?.transactions, appended, pagination],
  );

  const totals = totalsQuery.data ?? DEFAULT_TOTALS;

  const filterYears = useMemo<StringOptions[]>(
    () =>
      availableYears.map((year) => ({
        label: String(year),
        value: String(year),
      })),
    [availableYears],
  );

  const categoryOptions = useMemo<StringOptions[]>(
    () => [
      ...categoriesIncome.map((cat) => ({
        label: cat.label,
        value: cat.value,
      })),
      ...categoriesExpense.map((cat) => ({
        label: cat.label,
        value: cat.value,
      })),
    ],
    [categoriesIncome, categoriesExpense],
  );

  const loadMoreTransactions = useCallback(() => {
    if (lazyResult.isFetching || !pagination?.hasNext) {
      return;
    }
    fetchMore({ ...transactionParams, page: nextPage })
      .unwrap()
      .then((result) => {
        setAppended((prev) => [...prev, ...result.transactions]);
        setLatestPagination(result.pagination);
        setNextPage((prev) => prev + 1);
      })
      .catch((err) => notify.apiError(err));
  }, [
    fetchMore,
    transactionParams,
    nextPage,
    pagination?.hasNext,
    lazyResult.isFetching,
  ]);

  const refetch = useCallback(async () => {
    await base.refetch();
  }, [base]);

  const refetchTotals = useCallback(async () => {
    await totalsQuery.refetch();
  }, [totalsQuery]);

  const error = base.error
    ? (getErrorMessages(base.error as any)[0] ?? "Failed to load transactions")
    : null;

  return {
    transactionsData,
    totals,
    accounts,
    categoriesIncome,
    categoriesExpense,
    categoryOptions,
    filterYears,
    isLoading: base.isLoading || (base.isFetching && !base.data),
    isLoadingMore: lazyResult.isFetching,
    error,
    refetch,
    refetchTotals,
    loadMoreTransactions,
  };
};
